import React from 'react';
import cx from 'classnames';

import {
    Button,
    Icon,
    IconTypes,
    NotificationTooltip,
    Tooltip,
} from '@pexip/components';
import {useTranslation} from 'react-i18next';

import {TOOLTIP_Z_INDEX_BELOW_OVERLAYS} from '../../constants';
import {DeviceDeniedTooltip} from '../DeviceDeniedTooltip/DeviceDeniedTooltip.view';
import {TestId} from '../../../test/testIds';
import type {
    BlockedBrowserPermissionsInfoType,
    DeviceStatusInfo,
} from '../../types';
import {MuteDisabledTooltip} from '../MuteDisabledTooltip/MuteDisabledTooltip.view';

import {InputControl} from './InputControl.view';
import styles from './InMeetingInputControl.module.scss';

export const InMeetingInputControl: React.FC<
    React.ComponentProps<typeof InputControl> & {
        canMute?: boolean;
        isSuspended?: boolean;
        errorTooltipCloseBtnTestID?: string;
        errorTooltipTestID?: string;
        inputStatusInfo?: DeviceStatusInfo;
        learnHowToGrantAccessURL?: string;
        permissionInfoType?: BlockedBrowserPermissionsInfoType;
        requestDeviceAgain?: () => void;
        setShowHelpVideo?: React.Dispatch<React.SetStateAction<boolean>>;
        shouldShowTooltip?: boolean;
        toggleTooltip?: () => void;
        showDeniedDeviceTooltip?: boolean;
        tooltipClassName?: string;
        tooltipPosition?: 'top' | 'right' | 'bottom' | 'left';
        tooltipText?: React.ReactNode;
    }
> = ({
    canMute = true,
    errorTooltipCloseBtnTestID,
    errorTooltipTestID,
    inputStatusInfo,
    isSuspended,
    learnHowToGrantAccessURL,
    permissionInfoType,
    requestDeviceAgain,
    setShowHelpVideo,
    shouldShowTooltip = true,
    showDeniedDeviceTooltip = false,
    size = 'large',
    toggleTooltip,
    tooltipClassName,
    tooltipPosition = 'top',
    tooltipText,
    variant = 'translucent',
    ...props
}) => {
    const {t} = useTranslation();

    if (!canMute) {
        return (
            <MuteDisabledTooltip
                onClose={toggleTooltip}
                tooltipClassName={styles.autoHideTooltip}
                visible={shouldShowTooltip}
            >
                <InputControl
                    variant={variant}
                    size={size}
                    {...props}
                    onClick={toggleTooltip}
                />
            </MuteDisabledTooltip>
        );
    }

    if (
        inputStatusInfo?.error &&
        inputStatusInfo?.controls === 'permissions' &&
        setShowHelpVideo
    ) {
        return (
            <DeviceDeniedTooltip
                data-testid={TestId.TooltipDeniedDevice}
                deniedDevice={inputStatusInfo.error}
                learnHowToGrantAccessURL={learnHowToGrantAccessURL}
                permissionInfoType={permissionInfoType}
                visible={showDeniedDeviceTooltip}
                setShowHelpVideo={setShowHelpVideo}
                tooltipClassName={styles.autoHideTooltip}
            >
                <InputControl variant={variant} size={size} {...props} />
            </DeviceDeniedTooltip>
        );
    }

    if (inputStatusInfo?.error || inputStatusInfo?.warning) {
        return (
            <NotificationTooltip
                zIndex={TOOLTIP_Z_INDEX_BELOW_OVERLAYS}
                className={cx(styles.autoHideTooltip, tooltipClassName)}
                controlledIsVisible={shouldShowTooltip}
                controlledOnChange={toggleTooltip}
                ctaFn={
                    inputStatusInfo.retriable && requestDeviceAgain
                        ? requestDeviceAgain
                        : undefined
                }
                ctaText={
                    inputStatusInfo.retriable ? (
                        <Button
                            className="px-3"
                            isLoading={props.isLoading}
                            onClick={requestDeviceAgain}
                            size="small"
                            variant="primary"
                            enhancerStart={
                                <Icon
                                    colorScheme="dark"
                                    source={IconTypes.IconRefresh}
                                />
                            }
                        >
                            {t('common.try-again', 'Try again')}
                        </Button>
                    ) : undefined
                }
                header={inputStatusInfo.title}
                position="topEnd"
                testIdCloseButton={errorTooltipCloseBtnTestID}
                testIdInner={errorTooltipTestID}
                role="tooltip"
                text={inputStatusInfo.message}
                aria-label={props['aria-label']}
                closeButtonAriaLabel={t(
                    'common.close-tooltip',
                    'Close tooltip',
                )}
            >
                <InputControl
                    variant={isSuspended ? 'translucent' : variant}
                    size={size}
                    {...props}
                    onClick={toggleTooltip}
                />
            </NotificationTooltip>
        );
    }

    // The plain hover/focus label tooltip shows itself; it isn't gated by
    // `shouldShowTooltip`, which only controls the click-triggered variants
    // above.
    if (!tooltipText) {
        return <InputControl size={size} variant={variant} {...props} />;
    }

    return (
        <Tooltip
            text={tooltipText}
            position={tooltipPosition}
            className={cx(styles.autoHideTooltip, tooltipClassName)}
            role="tooltip"
            aria-label={props['aria-label']}
        >
            <InputControl size={size} variant={variant} {...props} />
        </Tooltip>
    );
};

export type InMeetingInputControlProps = React.ComponentProps<
    typeof InMeetingInputControl
>;
